
import React, { useState, useEffect } from 'react';
import { Sparkles, Plus, Trash2, Wand2, Book, Save, X, Loader2, Edit3, ChevronLeft, ArrowRight, Share2, Users, LayoutGrid, PenTool, LogOut } from 'lucide-react';
import { Button } from '../ui/Button';
import { FileUpload } from '../ui/FileUpload';
import { ResultViewer } from '../ResultViewer';
import { ShareModal } from '../ui/ShareModal';
import { ConfirmModal } from '../ui/ConfirmModal';
import { apiService } from '../../services/apiService';
import { auth } from '../../firebase';
import { TOOL_COSTS } from '../../constants';
import { EnhancerPrompt } from '../../types';
import { useToast } from '../ui/Toast';
import { logger } from '../../utils/logger';

interface ProductEnhancerProps {
  onCostIncurred: (cost: number) => Promise<void>;
  checkCredits: (cost: number) => boolean;
}

export const ProductEnhancer: React.FC<ProductEnhancerProps> = ({ onCostIncurred, checkCredits }) => {
  const { addToast } = useToast();
  
  // View State
  const [activeTab, setActiveTab] = useState<'enhancer' | 'library'>('enhancer');

  // Image State
  const [inputImages, setInputImages] = useState<string[]>([]);
  
  // Library Data
  const [savedPrompts, setSavedPrompts] = useState<EnhancerPrompt[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(true); // Initial state true to prevent flash

  // Workflow State
  const [selectedProductType, setSelectedProductType] = useState<string | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<EnhancerPrompt | null>(null);
  
  const [isCreatingType, setIsCreatingType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');

  const [isCreatingPrompt, setIsCreatingPrompt] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [generatedPromptText, setGeneratedPromptText] = useState('');
  const [newPromptName, setNewPromptName] = useState('');
  const [newPromptComment, setNewPromptComment] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Editing State
  const [editingPrompt, setEditingPrompt] = useState<EnhancerPrompt | null>(null);
  const [editName, setEditName] = useState('');
  const [editPromptText, setEditPromptText] = useState('');
  const [editComment, setEditComment] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Share State
  const [sharePromptId, setSharePromptId] = useState<string | null>(null);

  // Confirm Modal State
  const [confirmConfig, setConfirmConfig] = useState<{isOpen: boolean; title: string; description: string; onConfirm: () => Promise<void>; isDestructive: boolean;}>({
      isOpen: false, title: '', description: '', onConfirm: async () => {}, isDestructive: false
  });
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);

  // Execution State
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [results, setResults] = useState<{image: string, filename: string}[]>([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    fetchPrompts();
  }, [activeTab]); // Refetch when tab changes to ensure sync

  const fetchPrompts = async () => {
    setLoadingLibrary(true);
    try {
      const res = await apiService.getEnhancerPrompts();
      if (res.success) {
        setSavedPrompts(res.prompts);
      }
    } catch (e) {
      console.error("Failed to fetch prompts", e);
    } finally {
      setLoadingLibrary(false);
    }
  };

  // Derived state: Unique Product Types
  const uniqueProductTypes = Array.from(new Set(savedPrompts.map(p => p.productType))).sort();

  // Helper: Prompts for selected type
  const getPromptsForType = (type: string) => savedPrompts.filter(p => p.productType === type);

  const handleFilesSelect = (files: File[]) => {
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setInputImages(prev => [...prev, e.target!.result as string]);
          setResults([]); // Reset results on new upload
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setInputImages(prev => prev.filter((_, i) => i !== index));
  };

  // --- Logic: Create New Type ---
  const handleCreateType = () => {
      if (!newTypeName.trim()) return;
      const typeName = newTypeName.trim();
      setSelectedProductType(typeName);
      setIsCreatingType(false);
      setNewTypeName('');
      // Automatically go to prompt creation for this new type
      setIsCreatingPrompt(true);
  };

  // --- Logic: Prompt Generation ---
  const handleGeneratePrompt = async () => {
      if (!selectedProductType) return;
      setIsGenerating(true);
      // Instructions are now optional
      try {
          const res = await apiService.generateEnhancerPrompt(selectedProductType, customInstructions || "");
          if (res.success) {
              setGeneratedPromptText(res.prompt);
          } else {
              addToast("Generation failed", 'error');
          }
      } catch (e: any) {
          addToast("Error: " + e.message, 'error');
      } finally {
          setIsGenerating(false);
      }
  };

  const handleManualEntryToggle = () => {
      setShowManualEntry(true);
      setGeneratedPromptText(''); // Clear if starting manual
  };

  const handleSavePrompt = async () => {
      if (!selectedProductType || !newPromptName || !generatedPromptText) return;
      setIsSaving(true);
      try {
          const res = await apiService.saveEnhancerPrompt(selectedProductType, newPromptName, generatedPromptText, newPromptComment);
          if (res.success) {
              addToast("Style saved to library!", 'success');
              
              // Immediately fetch to update list in background
              await fetchPrompts();
              
              // Reset UI state
              setIsCreatingPrompt(false);
              setNewPromptName('');
              setNewPromptComment('');
              setCustomInstructions('');
              setGeneratedPromptText('');
              setShowManualEntry(false);
          }
      } catch (e: any) {
          addToast("Save failed: " + e.message, 'error');
      } finally {
          setIsSaving(false);
      }
  };

  // --- Logic: Editing Prompt ---
  const openEditModal = (prompt: EnhancerPrompt, e: React.MouseEvent) => {
      e.stopPropagation();
      setEditingPrompt(prompt);
      setEditName(prompt.name);
      setEditPromptText(prompt.prompt);
      setEditComment(prompt.comment || '');
  };

  const handleUpdatePrompt = async () => {
      if (!editingPrompt || !editName || !editPromptText) return;
      setIsUpdating(true);
      try {
          const res = await apiService.updateEnhancerPrompt(editingPrompt.id, editName, editPromptText, editComment);
          if (res.success) {
              addToast("Style updated!", 'success');
              await fetchPrompts(); // Refresh list
              setEditingPrompt(null); // Close modal
          }
      } catch (e: any) {
          addToast("Update failed: " + e.message, 'error');
      } finally {
          setIsUpdating(false);
      }
  };

  const handleDeletePrompt = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setConfirmConfig({
          isOpen: true,
          title: "Delete Style",
          description: "Permanently delete this prompt style? This cannot be undone.",
          isDestructive: true,
          onConfirm: async () => {
              setIsConfirmLoading(true);
              try {
                  await apiService.deleteEnhancerPrompt(id);
                  setSavedPrompts(prev => prev.filter(p => p.id !== id));
                  if (selectedPrompt?.id === id) setSelectedPrompt(null);
                  addToast("Deleted", 'info');
                  setConfirmConfig(prev => ({...prev, isOpen: false}));
              } catch (e: any) { 
                  addToast("Error: " + e.message, 'error'); 
              } finally {
                  setIsConfirmLoading(false);
              }
          }
      });
  };

  const handleRemoveSharedPrompt = (prompt: EnhancerPrompt, e: React.MouseEvent) => {
      e.stopPropagation();
      setConfirmConfig({
          isOpen: true,
          title: "Remove Shared Style",
          description: "Remove this shared style from your library? You can ask the owner to share it again if needed.",
          isDestructive: true,
          onConfirm: async () => {
              setIsConfirmLoading(true);
              const myEmail = auth.currentUser?.email;
              if (!myEmail) {
                  setIsConfirmLoading(false);
                  return;
              }
              
              try {
                  await apiService.revokeShareEnhancerPrompt(prompt.id, myEmail);
                  addToast("Removed from library", 'info');
                  setSavedPrompts(prev => prev.filter(p => p.id !== prompt.id));
                  if (selectedPrompt?.id === prompt.id) setSelectedPrompt(null);
                  setConfirmConfig(prev => ({...prev, isOpen: false}));
              } catch(e: any) {
                  addToast("Failed: " + e.message, 'error');
              } finally {
                  setIsConfirmLoading(false);
              }
          }
      });
  }

  // --- Logic: Sharing ---
  const handleShareClick = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setSharePromptId(id);
  };

  const handleShareConfirm = async (email: string) => {
      if (!sharePromptId) return;
      try {
          await apiService.shareEnhancerPrompt(sharePromptId, email);
          addToast("Shared successfully!", 'success');
          setSavedPrompts(prev => prev.map(p => {
              if (p.id === sharePromptId) {
                  const newShared = p.sharedWith ? [...p.sharedWith, email] : [email];
                  return { ...p, sharedWith: newShared };
              }
              return p;
          }));
      } catch (e: any) { addToast(e.message, 'error'); }
  };

  const handleRevokeShare = async (email: string) => {
      if (!sharePromptId) return;
      try {
          await apiService.revokeShareEnhancerPrompt(sharePromptId, email);
          addToast("Access removed", 'success');
          setSavedPrompts(prev => prev.map(p => {
              if (p.id === sharePromptId) {
                  const newShared = p.sharedWith?.filter(e => e !== email) || [];
                  return { ...p, sharedWith: newShared };
              }
              return p;
          }));
      } catch (e: any) { addToast(e.message, 'error'); }
  };

  // --- Logic: Enhancement Execution ---
  const handleEnhance = async () => {
    if (inputImages.length === 0) return;
    if (!selectedPrompt) {
        addToast("Please select a style first.", 'error');
        return;
    }

    const totalCost = inputImages.length * TOOL_COSTS.enhance;
    if (!checkCredits(totalCost)) return;

    setIsEnhancing(true);
    setProgress({ current: 0, total: inputImages.length });
    setResults([]);

    let successCount = 0;
    let lastErrorMessage = "";
    const newResults: {image: string, filename: string}[] = [];

    try {
        for (let i = 0; i < inputImages.length; i++) {
            const img = inputImages[i];
            
            try {
                const res = await apiService.enhanceProduct(img, selectedPrompt.prompt, selectedProductType || 'Product');
                if (res.success && res.image) {
                    newResults.push({ 
                        image: res.image, 
                        filename: `${(selectedProductType || 'product').replace(/\s+/g, '-').toLowerCase()}-${i+1}.png` 
                    });
                    successCount++;
                    logger.debug(`Image ${i} enhanced successfully`);
                } else {
                    lastErrorMessage = res.error || "Unknown error";
                    logger.error(`Failed image ${i}:`, lastErrorMessage);
                }
            } catch (e: any) {
                lastErrorMessage = e.message;
                logger.error(`Failed image ${i}`, e);
            }
            setProgress(prev => ({ ...prev, current: i + 1 }));
        }

        if (successCount > 0) {
            await onCostIncurred(successCount * TOOL_COSTS.enhance);
            setResults(newResults);
            addToast(`Successfully enhanced ${successCount} images`, 'success');
        } else {
            addToast(`Failed to enhance images: ${lastErrorMessage || "Please try again."}`, 'error');
        }

    } catch (e: any) {
        addToast("Error: " + e.message, 'error');
    } finally {
        setIsEnhancing(false);
    }
  };

  // Reusable Component: Prompt Selection Grid
  const PromptGrid = ({ type }: { type: string }) => {
      const prompts = getPromptsForType(type);
      return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
               <button 
                  onClick={() => { 
                      setSelectedProductType(type); 
                      setIsCreatingPrompt(true); 
                      // If in library, switch tab to enhancer so creation UI is visible
                      if(activeTab === 'library') {
                          setSelectedPrompt(null);
                          setActiveTab('enhancer');
                      }
                  }}
                  className="p-4 border-2 border-dashed border-primary-200 bg-primary-50 rounded-lg text-primary-700 font-medium hover:bg-primary-100 transition-all flex flex-col items-center justify-center gap-2 min-h-[120px]"
               >
                   <Wand2 size={24} />
                   Create New Style
               </button>
               {prompts.map(prompt => (
                   <div 
                      key={prompt.id}
                      onClick={() => setSelectedPrompt(prompt)}
                      className={`relative p-4 border rounded-lg cursor-pointer transition-all flex flex-col justify-between min-h-[120px] group ${selectedPrompt?.id === prompt.id ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50/50' : 'border-slate-200 hover:border-primary-300 hover:shadow-sm bg-white'}`}
                   >
                       <div>
                           <div className="flex justify-between items-start">
                               <h4 className="font-bold text-slate-800 text-sm">{prompt.name}</h4>
                               {prompt.isSharedWithMe && <Users size={12} className="text-purple-500" />}
                           </div>
                           <p className="text-xs text-slate-500 mt-1 line-clamp-2">{prompt.comment || "No comment"}</p>
                       </div>
                       
                       <div className="flex justify-between items-center mt-2" onClick={e => e.stopPropagation()}>
                           <div className="flex gap-1">
                               {!prompt.isSharedWithMe ? (
                                   <>
                                    <button onClick={(e) => openEditModal(prompt, e)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-primary-600" title="Edit">
                                        <Edit3 size={12} />
                                    </button>
                                    <button onClick={(e) => handleShareClick(prompt.id, e)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-primary-600" title="Share">
                                        <Share2 size={12} />
                                    </button>
                                    <button onClick={(e) => handleDeletePrompt(prompt.id, e)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-red-600" title="Delete">
                                        <Trash2 size={12} />
                                    </button>
                                   </>
                               ) : (
                                   // SHARED: Show Remove Button
                                   <button onClick={(e) => handleRemoveSharedPrompt(prompt, e)} className="p-1 hover:bg-slate-100 rounded text-slate-400 hover:text-red-600" title="Remove from Library">
                                        <LogOut size={12} />
                                   </button>
                               )}
                           </div>
                           <button onClick={() => setSelectedPrompt(prompt)} className={`text-xs px-2 py-1 rounded border ${selectedPrompt?.id === prompt.id ? 'bg-primary-600 text-white border-primary-600' : 'bg-white border-slate-200 hover:border-primary-300'}`}>
                               {selectedPrompt?.id === prompt.id ? 'Selected' : 'Select'}
                           </button>
                       </div>
                   </div>
               ))}
          </div>
      );
  };

  return (
    <div className="space-y-6">
       <ShareModal
          isOpen={!!sharePromptId}
          onClose={() => setSharePromptId(null)}
          onConfirm={handleShareConfirm}
          onRevoke={handleRevokeShare}
          currentSharedWith={savedPrompts.find(p => p.id === sharePromptId)?.sharedWith}
          title="Share Prompt Style"
          description="Allow another user to use this prompt style."
       />

       {/* Edit Prompt Modal */}
       {editingPrompt && (
           <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setEditingPrompt(null)}>
               <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
                   <div className="flex justify-between items-center mb-4">
                       <h3 className="text-lg font-bold text-slate-900">Edit Prompt Style</h3>
                       <button onClick={() => setEditingPrompt(null)}><X size={20} className="text-slate-400 hover:text-slate-600" /></button>
                   </div>
                   <div className="space-y-4">
                       <div>
                           <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Style Name</label>
                           <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                       </div>
                       <div>
                           <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Prompt Text</label>
                           <textarea value={editPromptText} onChange={(e) => setEditPromptText(e.target.value)} className="w-full h-32 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono" />
                       </div>
                       <div>
                           <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Comment</label>
                           <input type="text" value={editComment} onChange={(e) => setEditComment(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                       </div>
                       <div className="flex justify-end gap-2 pt-2">
                           <Button variant="secondary" onClick={() => setEditingPrompt(null)}>Cancel</Button>
                           <Button onClick={handleUpdatePrompt} isLoading={isUpdating}>Save Changes</Button>
                       </div>
                   </div>
               </div>
           </div>
       )}
       
       <ConfirmModal 
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig(prev => ({...prev, isOpen: false}))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        description={confirmConfig.description}
        isDestructive={confirmConfig.isDestructive}
        isLoading={isConfirmLoading}
        confirmText="Delete"
      />

       {/* Top Navigation Tabs */}
       <div className="flex border-b border-slate-200">
          <button 
            onClick={() => setActiveTab('enhancer')} 
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'enhancer' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            <Sparkles size={18} /> Enhancer
          </button>
          <button 
            onClick={() => setActiveTab('library')} 
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'library' ? 'border-primary-600 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            <Book size={18} /> Prompt Library
          </button>
       </div>

       {activeTab === 'enhancer' ? (
           <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
               {/* Left Col: Image Upload & Grid */}
               <div className="lg:col-span-5 space-y-4">
                   <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                       <h3 className="text-lg font-bold text-slate-900 mb-4">1. Upload Photos</h3>
                       <FileUpload 
                            onFileSelect={() => {}}
                            onFilesSelect={handleFilesSelect}
                            previewUrl={null} 
                            label="Upload product photos" 
                            subLabel="JPG/PNG. Multiple files supported." 
                            multiple={true}
                       />
                       
                       {inputImages.length > 0 && (
                           <div className="mt-4 grid grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                               {inputImages.map((img, idx) => (
                                   <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                                       <img src={img} className="w-full h-full object-cover" />
                                       <button 
                                          onClick={() => removeImage(idx)}
                                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                       >
                                           <X size={12} />
                                       </button>
                                   </div>
                               ))}
                           </div>
                       )}
                   </div>
               </div>

               {/* Right Col: Configuration */}
               <div className="lg:col-span-7 space-y-4">
                   <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm h-full flex flex-col">
                       <h3 className="text-lg font-bold text-slate-900 mb-4">2. Select Style from Library</h3>
                       
                       {/* STEP 1: SELECT PRODUCT TYPE */}
                       {!selectedProductType ? (
                           <div className="animate-fade-in">
                               <label className="block text-sm font-medium text-slate-700 mb-3">Product Category</label>
                               
                               {loadingLibrary ? (
                                   <div className="py-12 flex justify-center text-slate-400 gap-2 items-center border border-dashed border-slate-200 rounded-lg">
                                       <Loader2 className="animate-spin" /> Loading Library...
                                   </div>
                               ) : (
                                   <>
                                       <div className="grid grid-cols-2 gap-3 mb-4">
                                           {uniqueProductTypes.map(type => (
                                               <button 
                                                  key={type}
                                                  onClick={() => setSelectedProductType(type)}
                                                  className="p-3 border border-slate-200 rounded-lg text-left hover:border-primary-500 hover:bg-primary-50 transition-all font-medium text-slate-700 flex justify-between items-center group"
                                               >
                                                   {type}
                                                   <ArrowRight size={16} className="text-slate-300 group-hover:text-primary-500" />
                                               </button>
                                           ))}
                                           
                                           {!isCreatingType ? (
                                               <button 
                                                  onClick={() => setIsCreatingType(true)}
                                                  className="p-3 border-2 border-dashed border-slate-200 rounded-lg text-left hover:border-primary-400 hover:text-primary-600 transition-all font-medium text-slate-500 flex items-center justify-center gap-2"
                                               >
                                                   <Plus size={16} /> Create New Type
                                               </button>
                                           ) : (
                                               <div className="col-span-2 bg-slate-50 p-3 rounded-lg border border-primary-200 flex gap-2 items-center animate-fade-in">
                                                   <input 
                                                      autoFocus
                                                      type="text" 
                                                      placeholder="e.g. Leather Wallet" 
                                                      value={newTypeName}
                                                      onChange={(e) => setNewTypeName(e.target.value)}
                                                      className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-sm"
                                                      onKeyDown={(e) => e.key === 'Enter' && handleCreateType()}
                                                   />
                                                   <button onClick={handleCreateType} disabled={!newTypeName} className="bg-primary-600 text-white px-3 py-1.5 rounded text-xs font-bold">Add</button>
                                                   <button onClick={() => setIsCreatingType(false)} className="text-slate-400 hover:text-slate-600"><X size={16}/></button>
                                               </div>
                                           )}
                                       </div>
                                       {uniqueProductTypes.length === 0 && !isCreatingType && (
                                           <p className="text-sm text-slate-400 italic text-center py-4">No product types found. Create one to start.</p>
                                       )}
                                   </>
                               )}
                           </div>
                       ) : (
                           // STEP 2: SELECT PROMPT FOR TYPE
                           <div className="flex flex-col flex-1 animate-fade-in">
                               <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100">
                                   <button onClick={() => { setSelectedProductType(null); setSelectedPrompt(null); setIsCreatingPrompt(false); }} className="p-1 hover:bg-slate-100 rounded-full text-slate-500">
                                       <ChevronLeft size={20} />
                                   </button>
                                   <span className="font-bold text-slate-800">{selectedProductType}</span>
                                   <span className="text-slate-300">/</span>
                                   <span className="text-slate-500 text-sm">Select Style</span>
                               </div>

                               {!isCreatingPrompt ? (
                                   <div className="space-y-4">
                                       <PromptGrid type={selectedProductType} />
                                   </div>
                               ) : (
                                   // CREATE NEW PROMPT UI
                                   <div className="flex-1 flex flex-col bg-slate-50 rounded-lg p-4 border border-slate-200">
                                       <div className="flex justify-between mb-4">
                                           <h4 className="font-bold text-slate-800 flex items-center gap-2"><Sparkles size={16} className="text-primary-500"/> New Style Generator</h4>
                                           <button onClick={() => setIsCreatingPrompt(false)} className="text-xs text-slate-500 underline">Cancel</button>
                                       </div>
                                       
                                       <div className="space-y-4">
                                           {!showManualEntry && !generatedPromptText && (
                                               <div>
                                                   <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                                                       AI Instructions <span className="font-normal text-slate-400 normal-case">(Optional)</span>
                                                   </label>
                                                   <textarea 
                                                      value={customInstructions}
                                                      onChange={(e) => setCustomInstructions(e.target.value)}
                                                      placeholder="e.g. Make it look like it's floating in space, with neon blue lighting. (Leave empty for auto-enhance)"
                                                      className="w-full h-20 p-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                                   />
                                                   <div className="flex gap-3 mt-3">
                                                       <Button onClick={handleGeneratePrompt} isLoading={isGenerating} className="flex-1 py-2 text-xs">
                                                           <Wand2 size={14} /> Generate AI Prompt
                                                       </Button>
                                                       <Button onClick={handleManualEntryToggle} variant="secondary" className="flex-1 py-2 text-xs">
                                                           <PenTool size={14} /> Write Manually
                                                       </Button>
                                                   </div>
                                               </div>
                                           )}

                                           {(showManualEntry || generatedPromptText) && (
                                               <div className="animate-fade-in pt-4 border-t border-slate-200">
                                                   <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Technical Prompt (Editable)</label>
                                                   <textarea 
                                                      value={generatedPromptText}
                                                      onChange={(e) => setGeneratedPromptText(e.target.value)}
                                                      placeholder="Enter the detailed technical prompt here..."
                                                      className="w-full h-24 p-3 text-xs font-mono border border-primary-200 bg-white rounded-lg focus:ring-2 focus:ring-primary-500 mb-3"
                                                   />
                                                   
                                                   <div className="grid grid-cols-2 gap-3 mb-3">
                                                       <div>
                                                           <label className="block text-xs font-bold text-slate-500 mb-1">Style Name</label>
                                                           <input type="text" value={newPromptName} onChange={(e) => setNewPromptName(e.target.value)} placeholder="e.g. Cyberpunk Neon" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" />
                                                       </div>
                                                       <div>
                                                           <label className="block text-xs font-bold text-slate-500 mb-1">Comment</label>
                                                           <input type="text" value={newPromptComment} onChange={(e) => setNewPromptComment(e.target.value)} placeholder="e.g. Best for electronics" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg" />
                                                       </div>
                                                   </div>
                                                   
                                                   <Button onClick={handleSavePrompt} isLoading={isSaving} disabled={!newPromptName || !generatedPromptText} variant="secondary" className="w-full">
                                                       <Save size={16} /> Save to Library
                                                   </Button>
                                               </div>
                                           )}
                                       </div>
                                   </div>
                               )}

                               {selectedPrompt && !isCreatingPrompt && (
                                   <div className="mt-6 pt-6 border-t border-slate-100">
                                       <div className="bg-primary-50 p-3 rounded-lg border border-primary-100 mb-4 text-xs text-primary-800">
                                           <strong>Selected Style: {selectedPrompt.name}</strong>
                                           <p className="opacity-75 truncate mb-2">{selectedPrompt.comment}</p>
                                           <div className="bg-white/80 p-2 rounded border border-primary-200 font-mono text-[10px] text-slate-600 max-h-24 overflow-y-auto">
                                               {selectedPrompt.prompt}
                                           </div>
                                       </div>
                                       <Button 
                                          onClick={handleEnhance} 
                                          isLoading={isEnhancing} 
                                          disabled={inputImages.length === 0} 
                                          className="w-full py-4 text-lg shadow-lg shadow-primary-500/20"
                                      >
                                           {isEnhancing ? (
                                               `Processing ${progress.current}/${progress.total}...`
                                           ) : (
                                               <>
                                                 <Sparkles size={20} /> Enhance {inputImages.length} Image{inputImages.length !== 1 ? 's' : ''} ({inputImages.length * TOOL_COSTS.enhance} Credits)
                                               </>
                                           )}
                                       </Button>
                                   </div>
                               )}
                           </div>
                       )}
                   </div>
               </div>
           </div>
       ) : (
           // LIBRARY TAB
           <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm min-h-[500px]">
               <div className="flex justify-between items-center mb-6">
                   <h3 className="text-xl font-bold text-slate-900">All Saved Prompts</h3>
                   <button 
                      onClick={() => { setIsCreatingType(true); setActiveTab('enhancer'); setSelectedProductType(null); }}
                      className="text-sm bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 flex items-center gap-2"
                   >
                       <Plus size={16} /> Add New Category
                   </button>
               </div>

               {loadingLibrary ? (
                   <div className="py-20 flex justify-center text-slate-400 gap-2 items-center">
                       <Loader2 className="animate-spin" /> Loading Library...
                   </div>
               ) : uniqueProductTypes.length === 0 ? (
                   <div className="text-center py-20 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                       <Book size={48} className="mx-auto text-slate-300 mb-4" />
                       <p className="text-slate-500 font-medium">Your library is empty.</p>
                       <p className="text-sm text-slate-400">Go to the Enhancer tab to create your first product category and prompt.</p>
                   </div>
               ) : (
                   <div className="space-y-8">
                       {uniqueProductTypes.map(type => (
                           <div key={type} className="border border-slate-200 rounded-xl overflow-hidden">
                               <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                                   <h4 className="font-bold text-slate-800 flex items-center gap-2">
                                       <LayoutGrid size={18} className="text-slate-400" />
                                       {type}
                                       <span className="text-xs font-normal text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                                           {getPromptsForType(type).length} Styles
                                       </span>
                                   </h4>
                               </div>
                               <div className="p-6 bg-white">
                                   <PromptGrid type={type} />
                               </div>
                           </div>
                       ))}
                   </div>
               )}
           </div>
       )}

       {/* Results Area (Shared across tabs if results exist) */}
       {(results.length > 0 || isEnhancing) && (
           <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-fade-in">
               <h3 className="text-lg font-bold text-slate-900 mb-4">Enhancement Results</h3>
               <ResultViewer 
                  success={results.length > 0} 
                  images={results} 
               />
           </div>
       )}
    </div>
  );
};
